import { AfterViewInit, ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';

import * as day from 'dayjs';
import { FlexCheckInStatuses, FlexTimeActivityInstance, FlexTimePeriod } from 'libs/domain';
import { ReportTypes } from 'libs/domain';
import { BehaviorSubject, Observable, ReplaySubject, combineLatest, lastValueFrom } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';

import { QueryParamService } from '@app/src/app/shared/services/filtering/query-param.service';

import { CarouselStylePresets } from '@shared/components/tiles-carousel';
import { FlexTimeActivityInstanceService, FlexTimePeriodService } from '@shared/services/flex-time';
import { MediaBreakpoints, MediaService } from '@shared/services/media';

import { FlexTimeManagerMessages, FlexTimeManagerRoutes } from '../../constants';
import { FlexReportQueryParams } from '../ftm-reports/constants';
import {
  FlexDashboardQueryParams,
  flexDashboardQuerySchema,
  FlexTimePeriodTile,
  FtmDashboardMessages,
} from './constants';
import { FlexInstanceTile, FtmDashboardService } from './services';

const getActivitiesFromPeriods = (periods: FlexTimePeriod[]): FlexTimeActivityInstance[] => {
  return (periods || []).reduce((acc, period) => {
    return acc.concat(period.activityInstances || []);
  }, []);
};

@Component({
  selector: 'mg-ftm-dashboard',
  templateUrl: './ftm-dashboard.component.html',
  styleUrls: ['./ftm-dashboard.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [QueryParamService],
})
export class FtmDashboardComponent implements OnDestroy, OnInit, AfterViewInit {
  /** Constants */
  public readonly FTM_MESSAGES = FlexTimeManagerMessages;
  public readonly MESSAGES = FtmDashboardMessages;
  public readonly CAROUSEL_PRESETS = CarouselStylePresets;

  /** Behavior Subjects */
  private readonly _selectedTeacherHashSubj = new BehaviorSubject<string>('');
  public readonly selectedTeacherHash$ = this._selectedTeacherHashSubj.asObservable();

  /** General Observables */
  public readonly teacherHasActivity$: Observable<boolean>;
  private readonly _destroyed = new ReplaySubject<void>(1);

  /** Date */
  public readonly todaysDate = day().format('MMM Do, YYYY');

  public activeDate$: Observable<day.Dayjs> = this._queryService.useQueryParams(flexDashboardQuerySchema.parse).pipe(
    map(params => {
      return params.date;
    }),
  );

  public presetCarouselLayout$: Observable<CarouselStylePresets> = this._media.breakpoint$.pipe(
    map(breakpoint => {
      const isMobile: MediaBreakpoints[] = ['xsmall', 'small'];
      const isTablet: MediaBreakpoints[] = ['medium', 'medium-large'];

      if (isMobile.includes(breakpoint)) {
        return CarouselStylePresets.TWO_X_THREE;
      }
      if (isTablet.includes(breakpoint)) {
        return CarouselStylePresets.THREE_X_THREE;
      }

      return CarouselStylePresets.SIX_X_TWO;
    }),
  );

  public readonly periodOptions$ = this.ftmDashboard.flexPeriods$.pipe(
    map(periods => this._flexPeriods.formatPeriodsOptions(periods)),
  );

  public activityInstances$: Observable<FlexInstanceTile[]> = combineLatest([
    this.ftmDashboard.flexPeriods$,
    this.ftmDashboard.periodFilter$,
  ]).pipe(
    map(([flexPeriods, periodFilter]) => {
      if (!flexPeriods.length) return [];

      const filtered = periodFilter ? flexPeriods.filter(p => periodFilter === p.id) : flexPeriods;

      const activityInstances = getActivitiesFromPeriods(filtered);

      return activityInstances.map(a => {
        return this.ftmDashboard.mapInstanceToTile(
          a,
          flexPeriods.find(p => a.flexTimePeriodId === p.id),
        );
      });
    }),
  );

  public periodTiles$ = this.ftmDashboard.flexPeriods$.pipe(
    map(periods => {
      const periodsMapped = (periods || []).map(p => {
        const { totalRegistered, totalAbsentees } = this._flexInstancesService.getTotalCounts(p.activityInstances);
        const tile: FlexTimePeriodTile = {
          id: p.id,
          name: p.title,
          count: totalRegistered,
          absentees: totalAbsentees,
          date: day(p.date).format('DD/MM/YYYY'),
          startTime: p.startTime,
          endTime: p.endTime,
          totalSpaces: p.totalSpaces,
          dateObject: day(p.date),
          studentRegistrationDeadline: p.studentRegistrationDeadline,
          teacherRegistrationDeadline: p.teacherRegistrationDeadline,
        };

        return tile;
      });

      return periodsMapped;
    }),
  );

  /** Component Constructor */
  constructor(
    public ftmDashboard: FtmDashboardService,
    private _media: MediaService,
    private _flexPeriods: FlexTimePeriodService,
    private _flexInstancesService: FlexTimeActivityInstanceService,
    private _queryService: QueryParamService<FlexDashboardQueryParams>,
  ) {
    this.teacherHasActivity$ = combineLatest([this.selectedTeacherHash$, this.activityInstances$]).pipe(
      map(([teacherHash, activityList]) => {
        if (!teacherHash) return true;

        return !!activityList.find(activity => {
          return activity.teacherHash === teacherHash;
        });
      }),
    );
  }

  ngOnInit() {
    this.activeDate$.pipe(takeUntil(this._destroyed)).subscribe(date => {
      this.ftmDashboard.fetchData(true, date);
    });
  }

  ngAfterViewInit() {
    this.periodTiles$.pipe(takeUntil(this._destroyed)).subscribe(periods => {
      if (periods?.length > 0) {
        const firstPeriod = periods[0];
        this.setActivePeriod(firstPeriod.id);
      }
    });
  }

  ngOnDestroy(): void {
    this._destroyed.next();
    this._destroyed.complete();
  }

  public async setActivePeriod(periodId?: number): Promise<void> {
    if (!periodId) return;
    this.ftmDashboard.setPeriodFilter(periodId);
  }

  public async goToReports({
    periodId,
    activityId,
    goToAbsents,
    goToCheckedIn,
    goToUnregistered,
  }: {
    periodId?: number;
    activityId?: number;
    goToAbsents?: boolean;
    goToCheckedIn?: boolean;
    goToUnregistered?: boolean;
  }) {
    const date = await lastValueFrom(this.activeDate$.pipe(take(1)));

    const params: FlexReportQueryParams = {
      start_date: day(date).startOf('day'),
      end_date: day(date).endOf('day'),
      period_ids: periodId ? [periodId] : [],
      activity_ids: activityId ? [activityId] : [],
    };

    if (goToAbsents) {
      params.check_in_statuses = [FlexCheckInStatuses.ABSENT];
    }

    if (goToCheckedIn) {
      params.check_in_statuses = [FlexCheckInStatuses.IN, FlexCheckInStatuses.TARDY];
    }

    const route = [
      FlexTimeManagerRoutes.ROOT,
      FlexTimeManagerRoutes.REPORTS,
      goToUnregistered ? ReportTypes.FLEX_PERIOD_UNREGISTERED : ReportTypes.FLEX_PERIOD_REGISTERED,
    ];

    this._queryService.updateQueryParams<FlexReportQueryParams>(params, {}, route);
  }

  public onTeacherSelectChange(teacher?: any) {
    if (!teacher) this._selectedTeacherHashSubj.next('');
    else this._selectedTeacherHashSubj.next(teacher);
  }

  public dateChanged(date: day.Dayjs) {
    this._queryService.updateQueryParams({
      date,
    });
  }
}
